import React, { useMemo } from "react";
import { logout } from "../../services/auth";
import Button from "../common/Button";
import { useI18n } from "../../i18n/I18nProvider";

export type PharmacyModuleTab = "billing" | "collection" | "stock" | "suppliers" | "notes" | "letters";

interface PharmacyModuleHeaderProps {
    activeTab: PharmacyModuleTab;
    onSelectTab: (_tab: PharmacyModuleTab) => void;
}

export default function PharmacyModuleHeader({
    activeTab,
    onSelectTab,
}: PharmacyModuleHeaderProps) {
    const { t, formatDate } = useI18n();
    const items = useMemo(
        () =>
            [
                { key: "billing", label: t("pharmacy_tab_billing_dispense"), icon: "🧾" },
                { key: "collection", label: t("pharmacy_tab_sales_history"), icon: "📈" },
                { key: "stock", label: t("pharmacy_tab_stock_inventory"), icon: "📦" },
                { key: "suppliers", label: t("pharmacy_tab_suppliers"), icon: "💊" },
                { key: "notes", label: t("pharmacy_tab_drug_notes"), icon: "📝" },
                { key: "letters", label: t("pharmacy_tab_clinic_letters"), icon: "✉️" },
            ] as const,
        [t]
    );

    return (
        <header className="mod-window-header" style={{ padding: '0 24px', alignItems: 'center', height: '64px', background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '32px', height: '32px', background: '#3b82f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.2rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.4)' }}>
                    Rx
                </div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', letterSpacing: '-0.02em', WebkitBackgroundClip: 'text' }}>
                    {t("pharmacy_brand_name")}
                </div>
            </div>

            <nav className="mod-sub-nav" aria-label={t("pharmacy_navigation_aria")} style={{ flex: 1, justifyContent: 'center', borderBottom: 'none' }}>
                {items.map((item) => {
                    const isActive = activeTab === item.key;
                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`mod-sub-nav-item ${isActive ? "active" : ""}`}
                            onClick={() => onSelectTab(item.key)}
                            title={item.label}
                            aria-current={isActive ? "page" : undefined}
                            style={{ padding: '0 12px', fontSize: '0.9rem' }}
                        >
                            <span style={{ marginRight: '6px' }} aria-hidden="true">{item.icon}</span>
                            {item.label}
                        </button>
                    );
                })}
            </nav>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#10b981', borderRadius: '50%' }}></span>
                    {formatDate(new Date(), { weekday: "short", month: "short", day: "numeric" })}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { logout(); window.location.href = "/"; }}
                    title={t("common_logout")}
                    style={{ color: '#64748b' }}
                >
                    {t("common_logout")}
                </Button>
            </div>
        </header>
    );
}
